import React from "react";
import { Select, TextInput } from "flowbite-react";
import { AlertMessage } from "../../../atoms/AlertMessage";
import { Spinner } from "../../../atoms/Spinner";
import { ModalShell } from "../../../atoms/ModalShell";
import { StatusBadge } from "../../../atoms/StatusBadge";
import {
  HiCheck,
  HiCreditCard,
  HiExclamation,
  HiPencil,
  HiSearch,
} from "react-icons/hi";
import { CustomButton } from "../../../atoms/CustomButton";
import { Copy } from "../../../atoms/Copy";
import { DataTable } from "../../../atoms/DataTable";
import SideDrawer from "../../../atoms/SideDrawer";
import {
  BankTransferAccountDetail,
  CryptoAccountDetail,
  PayoutAccountResponseDTO,
  PayoutMethod,
  PayoutResponseDTO,
  PayoutStatus,
  PayoutStatusEnum,
  StripeConnectAccountDetail,
} from "../../../../services/paymentModule";
import {
  formatAmount,
  formatDate,
  formatDateTime,
  getAccountStatusPill,
  getMethodBadge,
  getStatusBadge,
  getStatusLabel,
} from "./payoutHelpers";

type SearchType = "payment_step" | "payment" | "payout" | "entity";

interface PayoutDrawerProps {
  open: boolean;
  onClose: () => void;
  payout: PayoutResponseDTO | null;

  results?: PayoutResponseDTO[] | null;
  resultsLoading?: boolean;
  onSelectResult?: (payout: PayoutResponseDTO) => void;

  onSearchById: (type: SearchType, id: string) => void;

  accountInfo: PayoutAccountResponseDTO | null;
  accountLoading: boolean;
  accountRequested: boolean;
  onViewAccount: (payout: PayoutResponseDTO) => void;

  showStatusModal: boolean;
  onOpenStatusUpdate: (payout: PayoutResponseDTO) => void;
  onCloseStatusModal: () => void;
  statusUpdateLoading: boolean;
  selectedPayoutForStatus: PayoutResponseDTO | null;
  newStatus: PayoutStatusEnum;
  onNewStatusChange: (status: PayoutStatusEnum) => void;
  providerReference: string;
  onProviderReferenceChange: (value: string) => void;
  onUpdateStatus: () => void;
}

const renderAccountDetails = (accountInfo: PayoutAccountResponseDTO) => {
  if (!accountInfo?.account_detail) return null;

  const detail = accountInfo.account_detail;

  if (accountInfo.payout_method === PayoutMethod.BANK_TRANSFER) {
    const bankDetail = detail as BankTransferAccountDetail;
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Account holder</p>
            <div className="text-sm font-semibold text-gray-900 bg-gray-100 p-2 rounded">
              <Copy target={bankDetail.account_holder} />
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Bank name</p>
            {bankDetail.bank_name ? (
              <div className="text-sm text-gray-900 bg-gray-100 p-2 rounded">
                <Copy target={bankDetail.bank_name} />
              </div>
            ) : (
              <p className="text-sm text-gray-900 p-2">N/A</p>
            )}
          </div>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">IBAN</p>
          <div className="text-sm font-mono text-gray-900 bg-gray-100 p-2 rounded">
            <Copy target={bankDetail.iban} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">BIC/SWIFT</p>
            {bankDetail.bic ? (
              <div className="text-sm font-mono text-gray-900 bg-gray-100 p-2 rounded">
                <Copy target={bankDetail.bic} />
              </div>
            ) : (
              <p className="text-sm font-mono text-gray-900 p-2">N/A</p>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Country</p>
            <p className="text-sm text-gray-900 p-2">
              {bankDetail.country || "N/A"}
            </p>
          </div>
        </div>
        {bankDetail.bank_address && (
          <div>
            <p className="text-xs text-gray-500 mb-1">Bank address</p>
            <div className="text-sm text-gray-900 bg-gray-100 p-2 rounded">
              <Copy target={bankDetail.bank_address} />
            </div>
          </div>
        )}
      </div>
    );
  }

  if (accountInfo.payout_method === PayoutMethod.CRYPTO) {
    const cryptoDetail = detail as CryptoAccountDetail;
    return (
      <div className="space-y-3">
        <div>
          <p className="text-xs text-gray-500 mb-1">Wallet address</p>
          <div className="text-sm font-mono text-gray-900 bg-gray-100 p-2 rounded break-all">
            <Copy target={cryptoDetail.wallet_address} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Network</p>
            <p className="text-sm text-gray-900">
              {cryptoDetail.network || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Currency</p>
            <p className="text-sm text-gray-900">
              {cryptoDetail.currency || "N/A"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (accountInfo.payout_method === PayoutMethod.STRIPE_CONNECT) {
    const stripeDetail = detail as StripeConnectAccountDetail;
    return (
      <div className="space-y-3">
        <div>
          <p className="text-xs text-gray-500 mb-1">Stripe account ID</p>
          <div className="text-sm font-mono text-gray-900 bg-gray-100 p-2 rounded">
            <Copy target={stripeDetail.stripeAccountId} />
          </div>
        </div>
      </div>
    );
  }

  return <p className="text-gray-500 text-sm">Unknown account type</p>;
};

const PayoutDrawer: React.FC<PayoutDrawerProps> = ({
  open,
  onClose,
  payout,
  results,
  resultsLoading,
  onSelectResult,
  onSearchById,
  accountInfo,
  accountLoading,
  accountRequested,
  onViewAccount,
  showStatusModal,
  onOpenStatusUpdate,
  onCloseStatusModal,
  statusUpdateLoading,
  selectedPayoutForStatus,
  newStatus,
  onNewStatusChange,
  providerReference,
  onProviderReferenceChange,
  onUpdateStatus,
}) => {
  const drawerFooter = (
    <>
      {payout && !(results && results.length > 0) && !resultsLoading && (
        <div className="sticky bottom-0 left-0 right-0 -mx-4 -mb-4 border-t border-gray-200 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <CustomButton
              onClick={() => onOpenStatusUpdate(payout)}
              variant="warning"
              size="sm"
              title="Update status"
              icon="pencilWhite"
            />
            <CustomButton
              onClick={() => onViewAccount(payout)}
              variant="outline"
              size="sm"
              title={accountRequested ? "Refresh account" : "View account"}
              icon="documentSearch"
              isLoading={accountLoading}
              disabled={accountLoading}
            />
          </div>
        </div>
      )}

      {showStatusModal && selectedPayoutForStatus && (
        <ModalShell
          show
          onClose={onCloseStatusModal}
          size="md"
          title={
            <span className="flex min-w-0 items-center gap-2">
              <HiPencil className="flex-shrink-0 text-amber-600" size={22} />
              <span className="truncate">Update payout status</span>
            </span>
          }
          footer={
            <>
              <CustomButton
                onClick={onCloseStatusModal}
                variant="secondary"
                size="sm"
                title="Cancel"
                disabled={statusUpdateLoading}
              />
              <CustomButton
                onClick={onUpdateStatus}
                variant="primary"
                size="sm"
                title="Update status"
                icon="check"
                isLoading={statusUpdateLoading}
                disabled={
                  statusUpdateLoading ||
                  newStatus === selectedPayoutForStatus.status ||
                  (newStatus === PayoutStatus.PAID && !providerReference.trim())
                }
              />
            </>
          }
        >
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">Net amount</span>
                <span className="text-sm font-medium text-gray-900 tabular-nums">
                  {formatAmount(
                    selectedPayoutForStatus.net_amount,
                    selectedPayoutForStatus.currency
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Current status</span>
                {getStatusBadge(selectedPayoutForStatus.status)}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New status
              </label>
              <Select
                value={newStatus}
                onChange={(e) =>
                  onNewStatusChange(e.target.value as PayoutStatusEnum)
                }
              >
                <option value={PayoutStatus.PENDING}>Pending</option>
                <option value={PayoutStatus.PROCESSING}>Processing</option>
                <option value={PayoutStatus.PAID}>Paid</option>
                <option value={PayoutStatus.FAILED}>Failed</option>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Transaction reference
                {newStatus === PayoutStatus.PAID ? (
                  <span className="text-red-500 ml-1">*</span>
                ) : (
                  <span className="text-gray-400 ml-1">(optional)</span>
                )}
              </label>
              <TextInput
                type="text"
                placeholder="e.g. bank transfer reference, transaction ID"
                value={providerReference}
                onChange={(e) => onProviderReferenceChange(e.target.value)}
                color={
                  newStatus === PayoutStatus.PAID && !providerReference.trim()
                    ? "failure"
                    : undefined
                }
              />
              <p className="text-xs text-gray-500 mt-1">
                {newStatus === PayoutStatus.PAID
                  ? "Required: enter a transaction reference."
                  : "Enter a transaction reference for tracking."}
              </p>
            </div>

            {newStatus === PayoutStatus.PAID &&
              selectedPayoutForStatus.status !== PayoutStatus.PAID && (
                <AlertMessage color="info" icon={HiCheck}>
                  <span className="font-medium">Marking as paid:</span> this
                  will indicate that the payment has been sent to the user.
                </AlertMessage>
              )}
            {newStatus === PayoutStatus.FAILED &&
              selectedPayoutForStatus.status !== PayoutStatus.FAILED && (
                <AlertMessage color="failure" icon={HiExclamation}>
                  <span className="font-medium">Marking as failed:</span> this
                  will indicate that the payment could not be processed.
                </AlertMessage>
              )}
          </div>
        </ModalShell>
      )}
    </>
  );

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={
        results && results.length > 0 ? "Payout results" : "Payout details"
      }
      icon={HiCreditCard}
      footer={drawerFooter}
    >
      <>
        {resultsLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size="xl" />
          </div>
        ) : results && results.length > 0 ? (
          <div className="flex flex-col gap-2 pb-24">
            <p className="text-sm tabular-nums text-gray-500">
              {results.length} payouts found. Select one to view its details.
            </p>
            {results.map((p) => (
              <button
                key={p.payout_identification}
                type="button"
                onClick={() => onSelectResult && onSelectResult(p)}
                className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3 text-left transition-colors hover:border-blue-400 hover:bg-blue-50"
              >
                <div className="flex flex-wrap items-center gap-2">
                  {getStatusBadge(p.status)}
                  {getMethodBadge(p.payout_method)}
                  <span className="ml-auto text-sm font-medium text-gray-900 tabular-nums">
                    {formatAmount(p.net_amount, p.currency)}
                  </span>
                </div>
                <div className="font-mono text-xs text-gray-700">
                  {p.payout_identification}
                </div>
                <div className="text-xs text-gray-500">
                  {formatDate(p.period_start)} - {formatDate(p.period_end)}
                </div>
              </button>
            ))}
          </div>
        ) : !payout ? (
          <p className="py-16 text-center text-sm text-gray-500">
            No payout selected.
          </p>
        ) : (
          <div className="flex h-full flex-col gap-5 pb-24">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                {getStatusBadge(payout.status)}
                {getMethodBadge(payout.payout_method)}
              </div>
              <p className="mb-1 text-xs text-gray-500">Payout ID</p>
              <div className="text-sm font-mono text-gray-900">
                <Copy target={payout.payout_identification} truncate />
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Entity ID</p>
                  <div className="text-sm font-mono text-gray-900">
                    <Copy target={payout.entity_identification} truncate />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Period</p>
                  <p className="text-sm text-gray-900">
                    {formatDate(payout.period_start)} -{" "}
                    {formatDate(payout.period_end)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Gross amount</p>
                  <p className="text-sm font-medium text-gray-900 tabular-nums">
                    {formatAmount(payout.gross_amount, payout.currency)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    Platform fee ({payout.platform_fee_rate / 100}%)
                  </p>
                  <p className="text-sm font-medium text-gray-900 tabular-nums">
                    {formatAmount(payout.platform_fee, payout.currency)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Net amount</p>
                  <p className="text-sm font-medium text-gray-900 tabular-nums">
                    {formatAmount(payout.net_amount, payout.currency)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Items</p>
                  <p className="text-sm font-medium text-gray-900 tabular-nums">
                    {payout.items?.length || 0} payment steps
                  </p>
                </div>
              </div>
            </div>

            {payout.items && payout.items.length > 0 && (
              <DataTable>
                <DataTable.Head>
                  <DataTable.HeadCell>Step ID</DataTable.HeadCell>
                  <DataTable.HeadCell>Payment ID</DataTable.HeadCell>
                  <DataTable.HeadCell numeric>Amount</DataTable.HeadCell>
                  <DataTable.HeadCell>Payment date</DataTable.HeadCell>
                </DataTable.Head>
                <DataTable.Body>
                  {payout.items.map((item) => (
                    <DataTable.Row key={item.payment_step_identification}>
                      <DataTable.Cell className="font-mono text-xs text-gray-900">
                        <div className="flex items-center gap-1">
                          <Copy
                            target={item.payment_step_identification}
                            truncate
                          />
                          <CustomButton
                            variant="none"
                            size="icon"
                            className="text-gray-400 enabled:hover:text-blue-600"
                            onClick={() =>
                              onSearchById(
                                "payment_step",
                                item.payment_step_identification
                              )
                            }
                            tooltip="Search by this step ID"
                            icon={<HiSearch size={14} />}
                          />
                        </div>
                      </DataTable.Cell>
                      <DataTable.Cell className="font-mono text-xs">
                        <div className="flex items-center gap-1">
                          <Copy target={item.payment_identification} truncate />
                          <CustomButton
                            variant="none"
                            size="icon"
                            className="text-gray-400 enabled:hover:text-blue-600"
                            onClick={() =>
                              onSearchById(
                                "payment",
                                item.payment_identification
                              )
                            }
                            tooltip="Search by this payment ID"
                            icon={<HiSearch size={14} />}
                          />
                        </div>
                      </DataTable.Cell>
                      <DataTable.Cell
                        numeric
                        className="whitespace-nowrap font-medium text-gray-900"
                      >
                        {formatAmount(item.amount, item.currency)}
                      </DataTable.Cell>
                      <DataTable.Cell className="whitespace-nowrap tabular-nums">
                        {formatDateTime(item.payment_date_time)}
                      </DataTable.Cell>
                    </DataTable.Row>
                  ))}
                </DataTable.Body>
              </DataTable>
            )}

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
                <HiCreditCard className="text-gray-500" size={20} />
                Account information
              </h4>
              {accountLoading ? (
                <div className="flex justify-center items-center py-8">
                  <Spinner size="lg" />
                </div>
              ) : !accountRequested ? (
                <div className="flex flex-col items-start gap-2">
                  <p className="text-sm text-gray-500">
                    Account details are not loaded yet.
                  </p>
                  <CustomButton
                    onClick={() => onViewAccount(payout)}
                    variant="outline"
                    size="sm"
                    title="Load account information"
                    icon="documentSearch"
                  />
                </div>
              ) : accountInfo ? (
                <div className="space-y-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-500">
                        Amount to pay
                      </span>
                      <span className="text-sm font-medium text-gray-900 tabular-nums">
                        {formatAmount(payout.net_amount, payout.currency)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {getMethodBadge(accountInfo.payout_method)}
                      {getAccountStatusPill(accountInfo.status)}
                      {accountInfo.is_default && (
                        <StatusBadge color="blue">Default</StatusBadge>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      Account information
                    </h4>
                    {renderAccountDetails(accountInfo)}
                  </div>

                  {accountInfo.status !== "ACTIVE" && (
                    <AlertMessage color="warning" icon={HiExclamation}>
                      <span className="font-medium">Warning!</span> This payout
                      account is not active. Status:{" "}
                      {getStatusLabel(accountInfo.status)}
                    </AlertMessage>
                  )}
                </div>
              ) : (
                <div className="py-8 text-center text-sm text-gray-500">
                  No account information available for this payout.
                </div>
              )}
            </div>
          </div>
        )}
      </>
    </SideDrawer>
  );
};

export default PayoutDrawer;
